import React from 'react';
import { useSnapshotData } from '../hooks/useSnapshotData';
import styles from '../style/Portfolios.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faTrophy, faMedal } from '@fortawesome/free-solid-svg-icons';

export default function HallOfFame({ navigate, t }) {
  const { 
    items, loading, currentPeriod, sortConfig, requestSort, moveWeek, maxPeriod, minPeriod,
  } = useSnapshotData('ret_1w');

  const get1wValue = (item, metric) => {
      // API 응답 구조에 맞춰 1w 지표 추출
      return item.metrics?.[metric]?.['1w'] ?? 0;
  };

  // 🏆 백엔드에서 제공하는 상대 랭킹 필드에 따라 배지 렌더링
  const renderTopTierBadges = (item) => {
    const badges = [];
    if (item.rank_ret > 0 && item.rank_ret <= 3) {
      badges.push(<span key="ret" className={`${styles.miniBadge} ${styles.badgeRet}`}>🔥 수익 TOP 3</span>);
    }
    if (item.rank_mdd > 0 && item.rank_mdd <= 3) {
      badges.push(<span key="mdd" className={`${styles.miniBadge} ${styles.badgeMdd}`}>🛡️ 방어 TOP 3</span>);
    }
    if (item.rank_sharpe > 0 && item.rank_sharpe <= 3) {
      badges.push(<span key="sh" className={`${styles.miniBadge} ${styles.badgeSharpe}`}>📊 효율 TOP 3</span>);
    }
    return badges;
  };

  // [추가] 현재 선택된 정렬 기준(sortConfig.key)에 따라 포디움에 표시할 값과 라벨 결정
  const getActiveMetricInfo = (item) => {
    const key = sortConfig.key || 'ret_1w'; // 기본값 수익률
    
    if (key.includes('ret')) {
      return {
        label: t('portfolios.tableHeaders.return'),
        value: `${(get1wValue(item, 'ret') * 100).toFixed(2)}%`,
      };
    } else if (key.includes('mdd')) {
      return {
        label: t('portfolios.tableHeaders.maxDrawdown'),
        value: `${Math.abs(get1wValue(item, 'mdd') * 100).toFixed(2)}%`,
      };
    } else if (key.includes('sharpe')) {
      return {
        label: t('portfolios.tableHeaders.sharpe'),
        value: get1wValue(item, 'sharpe').toFixed(2),
      };
    }
    return { label: '', value: '' };
  };

  // 상단 포디움 (리스트의 최상단 3명)
  const top3 = items.slice(0, 3);

  return (
    <div className={styles.hallOfFameContainer}>
      {/* 2. 주차 이동 컨트롤러 */}
      <div className={styles.periodNavigator}>
        <button onClick={() => moveWeek(-1)} disabled={currentPeriod <= minPeriod} className={styles.navBtn}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
        <span className={styles.periodTitle}>{currentPeriod}</span>
        <button onClick={() => moveWeek(1)} disabled={currentPeriod >= maxPeriod} className={styles.navBtn}>
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      </div>

      {/* 1. 상단 포디움 영역 */}
      {!loading && items.length > 0 && (
        <div className={styles.podiumContainer}>
          {top3.map((item, idx) => {
            const activeMetric = getActiveMetricInfo(item);
            return (
              <div 
                key={item.portfolioId} 
                className={`${styles.podiumCard} ${styles[`rank${idx + 1 === 1 ? '1st' : idx + 1 === 2 ? '2nd' : '3rd'}`]}`}
                /* ⭐ 카드 전체 클릭 시 유저 프로필로 이동 */
                onClick={() => navigate(`/users/${item.userId}/profile`)}
                style={{ cursor: 'pointer' }} 
              >
                <FontAwesomeIcon 
                  icon={idx === 0 ? faTrophy : faMedal} 
                  size="2x" 
                  style={{ color: idx === 0 ? '#ffd700' : idx === 1 ? '#c0c0c0' : '#cd7f32' }}
                />
                
                <div className={styles.podiumName}>
                  {item.nickname || item.userId}
                </div>
                
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <div className={styles.podiumValue}>{activeMetric.value}</div>
                  <div className={styles.podiumLabel}>{activeMetric.label}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 3. 테이블 리스트 영역 */}
      <div className={styles.portfolioTableWrapper}>
        <table className={styles.portfolioTable}>
          <thead>
            <tr>
              <th className={styles.rankHeader}></th>
              <th className={styles.userHeader}>User</th>
              <th>Portfolio</th>
              <th onClick={() => requestSort('ret_1w')} style={{cursor: 'pointer', color: sortConfig.key === 'ret_1w' ? '#ffd700' : 'inherit'}}>
                {t('portfolios.tableHeaders.return')} (1W)
              </th>
              <th onClick={() => requestSort('mdd_1w')} style={{cursor: 'pointer', color: sortConfig.key === 'mdd_1w' ? '#ffd700' : 'inherit'}}>
                {t('portfolios.tableHeaders.maxDrawdown')} (1W)
              </th>
              <th onClick={() => requestSort('sharpe_1w')} style={{cursor: 'pointer', color: sortConfig.key === 'sharpe_1w' ? '#ffd700' : 'inherit'}}>
                {t('portfolios.tableHeaders.sharpe')} (1W)
              </th>     
              <th></th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
               <tr><td colSpan="7" className={styles.loadingText}>Loading...</td></tr>
            ) : (
              items.map((item, index) => {
                const rank = index + 1;
                const ret = (get1wValue(item, 'ret') * 100).toFixed(2);
                const mdd = (get1wValue(item, 'mdd') * 100).toFixed(2);
                const sharpe = get1wValue(item, 'sharpe').toFixed(2);
                
                return (
                  <tr key={item.portfolioId} className={styles.portfolioRow}>
                    <td className={styles.rankCell}>
                      <span className={`${styles.rankBadge} ${rank === 1 ? styles.gold : rank === 2 ? styles.silver : rank === 3 ? styles.bronze : ''}`}>
                        {rank}
                      </span>
                    </td>
                    <td 
                      className={styles.userCell}
                      onClick={() => navigate(`/users/${item.userId}/profile`)}
                      style={{ cursor: 'pointer' }} // 클릭 가능함을 표시
                    >
                      <strong>{item.nickname || item.userId}</strong>
                      <div className={styles.badgeContainer}>
                        {renderTopTierBadges(item)}
                      </div>
                    </td> 
                    
                    <td className={styles.nameCell}>{item.portfolioName}</td> 
                    <td className={ret >= 0 ? styles.positive : styles.negative}>{ret}%</td>
                    <td className={styles.negative}>{Math.abs(mdd)}%</td>
                    <td className={sharpe >= 0 ? styles.positive : styles.negative}>{sharpe}</td>
                    <td>
                      <button onClick={()=>navigate(`/portfolios/${item.portfolioId}`)} className={styles.btnView}>View</button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}